import { ThoughtShareWith } from '@api/assessmentQuestionnaireApiHandler';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch } from '@hooks/useAppDispatch';
import useWindowDimensions from '@hooks/useWindowDimensions';
import { Box, Stack, TextField } from '@mui/material';
import { BorderedCheckbox, CustomSwipeableDrawer, MotionText, showToast } from '@shared/atoms';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { addThought } from 'store/Slices/QuestionnaireSliceV2';
import * as yup from 'yup';
import { SystemButton } from '../SystemButton/SystemButton';
import styles from './index.module.scss';

const schema = yup.object().shape({
  thoughts: yup.string().max(280, 'Maximum 280 characters allowed'),
  shareWith: yup.string().required('Please select an option'),
});

export const ShareThoughtDrawer: React.FC<ShareThoughtDrawerInterface> = ({
  open,
  handleClose,
  handleOpen,
  questionId,
  questionLabel,
}) => {
  const dispatch = useAppDispatch();
  const { control, handleSubmit, watch, setValue, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      thoughts: '',
      shareWith: ThoughtShareWith.MYSELF,
    },
  });
  const { isDesktop } = useWindowDimensions();

  const onSubmit = (data: {
    thoughts: string;
    shareWith: ThoughtShareWith.COMPANY | ThoughtShareWith.MYSELF;
  }) => {
    dispatch(
      addThought({
        thought: data.thoughts,
        shareWith: data.shareWith,
        questionId,
      }),
    );
    showToast('Your thought have been saved', undefined, 'success');
    reset();
    handleClose();
  };

  const thoughts = watch('thoughts');
  const shareWith = watch('shareWith');

  const handleMyselfChange = () => {
    setValue('shareWith', ThoughtShareWith.MYSELF);
  };

  const handleCompanyChange = () => {
    setValue('shareWith', ThoughtShareWith.COMPANY);
  };

  return (
    <CustomSwipeableDrawer
      open={open}
      onClose={() => {
        reset();
        handleClose();
      }}
      onOpen={handleOpen}
      anchor="right"
      customWidth={isDesktop ? '400px' : '100vw'}
    >
      <Box className={styles.container} sx={{ p: 3 }}>
        <MotionText as="h2" text="Share your thoughts" />
        <MotionText as="p" text={questionLabel} />

        {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">General happiness</Typography>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              border: '2px solid #007BFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              color: '#007BFF',
            }}
          >
            5
          </Box>
        </Box> */}

        <Controller
          name="thoughts"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              placeholder="What’s on your mind?"
              multiline
              rows={6}
              fullWidth
              variant="outlined"
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 280 }}
            />
          )}
        />
        <p className={styles.hsCharCounter}>{thoughts.length}/280 characters</p>

        <MotionText as="p" text="Share with:" />

        <Stack direction={isDesktop ? 'row' : 'column'} spacing={2} sx={{ mb: 2 }}>
          <BorderedCheckbox
            id="mySelf"
            text="Myself"
            color="#019DF2"
            onChange={handleMyselfChange}
            checked={shareWith === ThoughtShareWith.MYSELF}
          />
          <BorderedCheckbox
            id="company"
            text="Organisation"
            color="#019DF2"
            onChange={handleCompanyChange}
            checked={shareWith === ThoughtShareWith.COMPANY}
          />
        </Stack>

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <SystemButton
            variant="primarySkyBlue"
            onClick={handleSubmit(onSubmit)}
            disabled={!thoughts || !shareWith}
            fullWidthButton
          >
            Share thought
          </SystemButton>
        </Box>
      </Box>
    </CustomSwipeableDrawer>
  );
};

type ShareThoughtDrawerInterface = {
  open: boolean;
  handleClose: () => void;
  handleOpen: () => void;
  questionId: number;
  questionLabel: string;
};
